// ItemDeliveryTab — shows delivery history for a single item and surfaces calibration signals.

import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  History,
  RotateCcw,
} from 'lucide-react';
import { getDeliveriesForItem } from '@/lib/itemDeliveryService';
import type { ItemDelivery, DeliveryType } from '@/types/delivery';
import { DELIVERY_TYPE_LABELS } from '@/types/delivery';

interface Props {
  itemId: string;
  refreshTrigger?: number;
  onMarkDelivered: () => void;
  onReviseDelivery?: (currentOutcome: DeliveryType) => void;
}

const TYPE_ICON = {
  shipped: CheckCircle2,
  partial: Clock,
  cancelled: XCircle,
} as const;

const TYPE_COLOR = {
  shipped: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  partial: 'text-[var(--mustard)]',
  cancelled: 'text-[var(--coral)]',
} as const;

const TYPE_BG = {
  shipped: 'bg-[var(--green-dim)] border-[var(--green)]',
  partial: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',
  cancelled: 'bg-[var(--coral-dim)] border-[var(--coral)]',
} as const;

function AccuracyBadge({ accuracy }: { accuracy: number | null }) {
  if (accuracy == null) return null;
  const pct = Math.round(accuracy * 100);
  const Icon = pct >= 90 ? TrendingUp : pct >= 70 ? Minus : TrendingDown;
  const color = pct >= 90 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : pct >= 70 ? 'text-[var(--mustard)]' : 'text-[var(--coral)]';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {pct}% accuracy
    </span>
  );
}

function DeliveryCard({
  delivery,
  variant = 'default',
}: {
  delivery: ItemDelivery;
  variant?: 'current' | 'history' | 'default';
}) {
  const Icon = TYPE_ICON[delivery.deliveryType];
  const isHistory = variant === 'history';

  return (
    <div
      className={` border p-4 ${
        isHistory
          ? 'bg-[var(--surface)]/80 border-[var(--rule)] opacity-90'
          : TYPE_BG[delivery.deliveryType]
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${isHistory ? 'text-[var(--text-ter)]' : TYPE_COLOR[delivery.deliveryType]}`} />
          <span className={`text-sm font-semibold ${isHistory ? 'text-[var(--text-sec)]' : 'text-[var(--text-pri)]'}`}>
            {DELIVERY_TYPE_LABELS[delivery.deliveryType]}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--text-ter)]">
          <AccuracyBadge accuracy={delivery.scoreAccuracy} />
          <span>{new Date(delivery.deliveredAt).toLocaleDateString()}</span>
        </div>
      </div>

      {(delivery.predictedScores?.numericScore != null || delivery.actualMetrics?.score != null) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {delivery.predictedScores?.numericScore != null && (
            <div className="p-2.5 bg-[var(--bg-raised)]/60 border border-white">
              <p className="text-xs text-[var(--text-ter)] mb-0.5">Predicted</p>
              <p className="text-base font-semibold text-[var(--text-sec)]">
                {delivery.predictedScores.numericScore.toFixed(1)}
                {delivery.predictedScores.toolName && (
                  <span className="text-xs font-normal text-[var(--text-ter)] ml-1">
                    ({delivery.predictedScores.toolName.replace(/-/g, ' ')})
                  </span>
                )}
              </p>
            </div>
          )}
          {delivery.actualMetrics?.score != null && (
            <div className="p-2.5 bg-[var(--bg-raised)]/60 border border-white">
              <p className="text-xs text-[var(--text-ter)] mb-0.5">Actual</p>
              <p className="text-base font-semibold text-[var(--text-sec)]">
                {Number(delivery.actualMetrics.score).toFixed(1)}
              </p>
            </div>
          )}
        </div>
      )}

      {delivery.notes && (
        <p className="text-sm text-[var(--text-sec)] mb-2">{delivery.notes}</p>
      )}
      {delivery.lessonsLearned && (
        <div className="mt-2 pt-2 border-t border-white/50">
          <p className="text-xs font-semibold text-[var(--text-ter)] mb-0.5">Lessons learned</p>
          <p className="text-sm text-[var(--text-sec)]">{delivery.lessonsLearned}</p>
        </div>
      )}
    </div>
  );
}

export function ItemDeliveryTab({
  itemId,
  refreshTrigger = 0,
  onMarkDelivered,
  onReviseDelivery,
}: Props) {
  const [deliveries, setDeliveries] = useState<ItemDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const loadDeliveries = useCallback(() => {
    setLoading(true);
    setErr(null);
    return getDeliveriesForItem(itemId)
      .then((data) => setDeliveries(data))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load deliveries'))
      .finally(() => setLoading(false));
  }, [itemId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDeliveriesForItem(itemId)
      .then((data) => { if (!cancelled) setDeliveries(data); })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load deliveries'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId, refreshTrigger]);

  const latestDelivery = deliveries[0] ?? null;
  const historyDeliveries = deliveries.slice(1);
  const hasDelivery = deliveries.length > 0;
  const handleRevise = () => {
    if (!latestDelivery) return;
    if (onReviseDelivery) onReviseDelivery(latestDelivery.deliveryType);
    else onMarkDelivered();
  };

  return (
    <div className="mt-1 space-y-5">
      {/* The section heading ("What was delivered") is the page's; this starts at the content. */}
      {!loading && hasDelivery && (
        <div className="flex items-start justify-between gap-4">
          <p className="text-xs text-[var(--text-ter)] max-w-md">
            Delivery records are append-only — a revision adds a new entry and updates calibration; it does not undo history.
          </p>
          <button
            type="button"
            onClick={handleRevise}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)] transition-all"
          >
            <RotateCcw className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            Record revision
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p className="text-sm text-[var(--text-ter)] animate-pulse">Loading delivery history…</p>
      )}

      {/* Error */}
      {err && (
        <div className="flex gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{err}</p>
            <button
              type="button"
              onClick={() => void loadDeliveries()}
              className="mt-1 text-xs font-semibold text-[var(--coral)] underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Nothing recorded yet: one line, one button */}
      {!loading && !err && !hasDelivery && (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-[var(--rule)] py-2.5">
          <span className="w-[170px] shrink-0 text-sm text-[var(--text-dim)]">Outcome</span>
          <span className="min-w-0 flex-1 text-sm text-[var(--mustard)]">
            Not recorded yet.{' '}
            <span className="text-[var(--text-dim)]">Record it when the item ships, is partly delivered or is cancelled.</span>
          </span>
          <button
            type="button"
            onClick={onMarkDelivered}
            className="font-mono text-[11px] uppercase tracking-[0.07em] text-[var(--text-sec)] underline decoration-[var(--rule-bright)] underline-offset-[3px] hover:text-[var(--text-pri)]"
          >
            Mark as delivered
          </button>
        </div>
      )}

      {/* Current outcome */}
      {!loading && latestDelivery && (
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-ter)]">
            Current outcome
          </p>
          <DeliveryCard delivery={latestDelivery} variant="current" />
        </div>
      )}

      {/* What revision means */}
      {!loading && hasDelivery && (
        <div className="flex gap-3 p-3 bg-[var(--surface)] border border-[var(--rule)]">
          <History className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-[var(--text-sec)] space-y-1">
            <p className="font-semibold text-[var(--text-sec)]">Need to change the outcome?</p>
            <p>
              Use <span className="font-medium">Record revision</span> if scope changed after shipping,
              a partial delivery became full, or the item was later cancelled. Each revision is timestamped
              and kept in history. Calibration uses the most recent record for this item.
            </p>
          </div>
        </div>
      )}

      {/* History */}
      {!loading && historyDeliveries.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setHistoryExpanded((v) => !v)}
            className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            <History className="h-3.5 w-3.5" />
            {historyExpanded ? 'Hide' : 'Show'} {historyDeliveries.length} earlier{' '}
            {historyDeliveries.length === 1 ? 'record' : 'records'}
          </button>
          {historyExpanded && (
            <div className="space-y-3">
              {historyDeliveries.map((d) => (
                <DeliveryCard key={d.id} delivery={d} variant="history" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calibration hint */}
      {latestDelivery?.scoreAccuracy != null && (
        <div className="flex gap-3 p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <TrendingUp className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-[var(--text-sec)] mb-0.5">Calibration data collected</p>
            <p className="text-xs text-[var(--text-sec)]">
              This item&apos;s predicted vs actual score is included in your workspace&apos;s calibration dashboard.
              Visit <a href="/prioritization/calibration" className="underline">Calibration</a> to see accuracy trends across all items.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}